import * as React from 'react';
import { map, compact } from 'lodash';

import Avatar from 'components/Avatar';
import Glyph from 'components/Glyph';
import GroupAvatar from 'groups/components/GroupAvatar';
import GroupChatAvatar from 'chat/components/GroupChatAvatar';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import OrgAvatar from 'organizations/components/OrgAvatar';
import Text from 'components/Text';
import Tooltip from 'components/Tooltip';
import { RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import UserAvatar from 'users/components/UserAvatar';
import errorLogger from 'utils/errorLogger';
import { Recipient } from 'types/typescript/messagingTypes';

const TRUNCATED_NAME_SIZE = 40;

const getDisplayProps = (s: Recipient) => {
  switch (s.type) {
    case RECIPIENT_TYPES.ORGANIZATION_RECIPIENT:
      return {
        name: s.organization.name,
        avatar: <OrgAvatar size="xsmall" />,
      };
    case RECIPIENT_TYPES.GROUP_RECIPIENT:
      return {
        name: s.group.className,
        avatar: <GroupAvatar group={s.group} size="xsmall" />,
      };
    case RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT:
      return {
        name: s.chat.getTitle(),
        avatar: <GroupChatAvatar color="whiteout" size="xsmall" />,
      };
    case RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT:
      return {
        name: s.member.getName(),
        avatar: (
          <UserAvatar user={s.member.getUser().toJSON()} size="xsmall" color="whiteout" hideBadge />
        ),
      };
    case RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT:
      return {
        name: s.csvName,
        avatar: <Avatar color="whiteout" glyphName="spreadsheet" size="xsmall" />,
      };
    default:
      return null;
  }
};

const getSelectedRoles = (recipient: Recipient) => {
  switch (recipient.type) {
    case RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT:
      return compact([
        recipient.includeParents && 'Parents',
        recipient.includeStudents && 'Students',
      ]);
    case RECIPIENT_TYPES.ORGANIZATION_RECIPIENT:
    case RECIPIENT_TYPES.GROUP_RECIPIENT:
      return map(recipient.selectedFilters, (filter) => filter.value.label);
    default:
      return [];
  }
};

function Token({ recipient }: { recipient: Recipient }) {
  const displayProps = getDisplayProps(recipient);
  if (!displayProps) {
    errorLogger(`Could not render token for recipient: invalid type ${recipient.type}`);
    return null;
  }

  const recipientId = recipient.id;
  const { name, avatar } = displayProps;
  const selectedRoles = getSelectedRoles(recipient);

  let displayName = name;
  if (name && name.length > TRUNCATED_NAME_SIZE) {
    // this is the officially character code for the ellipses. And the official
    // way to get it to display properly with the Text component. How fun.
    displayName = name.substr(0, TRUNCATED_NAME_SIZE) + String.fromCharCode(8230);
  }

  const tooltip = (
    <Tooltip>
      {name}
      {selectedRoles && selectedRoles.length ? (
        <Text size="xxxsmall" color="ruler" element="div">
          {selectedRoles.join(', ')}
        </Text>
      ) : null}
    </Tooltip>
  );
  return (
    <LayerTrigger layer={tooltip} placement="top" alignment="middle" key={recipientId}>
      <span
        className="ComposeMessageRecipientsInput-token"
        data-test-selector={`split-composer-token-${recipientId}`}
      >
        <MediaObject center gutter="xsmall">
          {avatar}
          {selectedRoles && selectedRoles.length ? (
            <React.Fragment>
              <Text className="ComposeMessageRecipientsInput-token-name" size="xsmall">
                {displayName}
              </Text>
              <Glyph name="chevron-right" color="graphite" size="xxsmall" />
              <Text
                className="ComposeMessageRecipientsInput-token-roles"
                data-test="selected-roles"
                size="xsmall"
                truncate
              >
                {selectedRoles.join(', ')}
              </Text>
            </React.Fragment>
          ) : (
            <Text className="ComposeMessageRecipientsInput-token-text" size="xsmall">
              {displayName}
            </Text>
          )}
        </MediaObject>
      </span>
    </LayerTrigger>
  );
}

export default Token;
